import React, { useState, useEffect } from 'react';
import { PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js';
import axios from 'axios';

const MF_API = process.env.REACT_APP_MAGIC_FOREST_API_URL || 'https://magicforest.land';

/**
 * ApplePayButton — Apple Pay / Google Pay wallet payment.
 *
 * Always renders as a selectable option in the payment method list.
 * When wallet is available → real payment flow.
 * When not available → shows "not available on this device" hint.
 *
 * Props.onAvailabilityChange(available) — reports wallet availability to parent.
 */
function ApplePayButton({
  amount, currency, donationType, referrer,
  onSuccess, onError, donorEmail, onBillingDetailsCollected, gdprConsent,
  isSelected, onSelect,
}) {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [canUseApplePay, setCanUseApplePay] = useState(false);
  const [canUseGooglePay, setCanUseGooglePay] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isButtonEnabled = () => {
    if (!gdprConsent) return false;
    if (donationType === 'monthly') return donorEmail && validateEmail(donorEmail);
    return true;
  };

  useEffect(() => {
    if (!stripe) return;
    setIsLoading(true);

    const pr = stripe.paymentRequest({
      country: 'ES',
      currency: currency.toLowerCase(),
      total: { label: 'Magic Forest Donation', amount: amount * 100 },
      requestPayerName: true,
      requestPayerEmail: false,
    });

    pr.canMakePayment().then((result) => {
      const apple = result && result.applePay;
      const google = result && result.googlePay;
      setCanUseApplePay(apple);
      setCanUseGooglePay(google);
      if (apple || google) {
        setCanMakePayment(true);
        setPaymentRequest(pr);
      }
      setIsLoading(false);
    }).catch(() => setIsLoading(false));

    pr.on('paymentmethod', async (ev) => {
      setIsProcessing(true);
      const billingDetails = ev.paymentMethod.billing_details || {};
      const collectedName = billingDetails.name || '';
      const emailToUse = donorEmail;

      if (onBillingDetailsCollected && collectedName) {
        onBillingDetailsCollected({ name: collectedName, email: emailToUse });
      }

      try {
        if (donationType === 'monthly') {
          if (!emailToUse || !emailToUse.trim()) {
            ev.complete('fail');
            onError && onError({ message: 'Email is required for monthly subscriptions' });
            setIsProcessing(false);
            return;
          }
          const response = await axios.post(`${MF_API}/api/create-subscription`, {
            amount, email: emailToUse, name: collectedName,
            payment_method_id: ev.paymentMethod.id, referrer: referrer || 'none',
          });
          const { clientSecret, subscriptionId } = response.data;
          if (!clientSecret) {
            ev.complete('success');
            onSuccess && onSuccess({ subscriptionId, type: 'subscription', status: 'active', billingDetails: { name: collectedName, email: emailToUse } });
            return;
          }
          const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);
          if (error) { ev.complete('fail'); onError && onError(error); }
          else { ev.complete('success'); onSuccess && onSuccess({ ...paymentIntent, subscriptionId, type: 'subscription', billingDetails: { name: collectedName, email: emailToUse } }); }
        } else {
          const response = await axios.post(`${MF_API}/api/create-payment-intent`, {
            amount, currency, donation_type: donationType, referrer: referrer || 'none',
          });
          const { client_secret } = response.data;
          const { error, paymentIntent } = await stripe.confirmCardPayment(client_secret, { payment_method: ev.paymentMethod.id });
          if (error) { ev.complete('fail'); onError && onError(error); }
          else { ev.complete('success'); onSuccess && onSuccess({ ...paymentIntent, billingDetails: { name: collectedName, email: emailToUse } }); }
        }
      } catch (error) {
        ev.complete('fail');
        onError && onError({ message: error.response?.data?.detail || error.message || 'Payment failed' });
      } finally {
        setIsProcessing(false);
      }
    });

    pr.on('cancel', () => {
      setIsProcessing(false);
    });
  }, [stripe, amount, currency, donationType, referrer, onSuccess, onError]);

  const handleLaunchPay = async () => {
    if (donationType === 'monthly' && !isButtonEnabled()) {
      onError && onError({ message: 'Please enter a valid email address first' });
      return;
    }
    if (paymentRequest && !isProcessing) {
      try { setIsProcessing(true); await paymentRequest.show(); }
      catch { setIsProcessing(false); }
    }
  };

  // Determine what's available
  const isApple = /iPhone|iPad|iPod|Mac/.test(navigator.userAgent);
  const walletLabel = isApple ? 'Apple Pay' : 'Google Pay';
  const available = canMakePayment;
  const enabled = isButtonEnabled() && available;

  // Apple icon
  const AppleIcon = () => (
    <svg className="h-4 w-4 mr-2.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );

  // Google Pay icon
  const GoogleIcon = () => (
    <svg className="h-4 w-4 mr-2.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );

  return (
    <>
      {/* Always-visible selectable row */}
      <div
        className={`bg-neutral-800/50 p-2.5 rounded-lg flex items-center text-sm cursor-pointer transition-colors ${
          isSelected ? 'ring-1 ring-emerald-500 text-white' : 'text-white/70 hover:text-white'
        }`}
        onClick={onSelect}
      >
        {isApple ? <AppleIcon /> : <GoogleIcon />}
        {walletLabel}
        {isLoading && <span className="ml-auto text-white/30 text-xs">checking…</span>}
        {!isLoading && !available && <span className="ml-auto text-white/30 text-xs">not on this device</span>}
      </div>

      {/* Expanded content when selected */}
      {isSelected && (
        <div className="p-3 bg-neutral-900/50 rounded-lg border border-neutral-700/50">
          {isLoading ? (
            <p className="text-white/40 text-xs text-center py-2">Checking wallet availability…</p>
          ) : !available ? (
            <div className="text-center py-2">
              <p className="text-white/50 text-sm mb-1">{walletLabel} is not available on this device</p>
              <p className="text-white/35 text-xs">
                {isApple
                  ? 'Use Safari on an Apple device with Apple Pay configured.'
                  : 'Use Chrome on a device with Google Pay configured.'}
              </p>
            </div>
          ) : (
            <div>
              <button
                type="button"
                className={`w-full py-2.5 rounded-lg font-medium text-sm transition-all ${
                  enabled && !isProcessing
                    ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                    : 'bg-neutral-700 text-neutral-500 cursor-not-allowed'
                }`}
                onClick={handleLaunchPay}
                disabled={!enabled || isProcessing}
              >
                {isProcessing ? 'Processing…' : `Pay with ${walletLabel}`}
              </button>
              {donationType === 'monthly' && !isButtonEnabled() && (
                <p className="text-amber-400 text-xs mt-2 text-center">Enter email above to enable {walletLabel}</p>
              )}
            </div>
          )}
          {/* Hidden native element for Stripe */}
          {paymentRequest && (
            <div className="sr-only">
              <PaymentRequestButtonElement options={{ paymentRequest }} />
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default ApplePayButton;
